import {
  zoneBoxLabel,
  type ZoneParams,
  type ZoneRectangle,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import {
  GeckoComponentRect,
  GeckoMarkupLayer,
  GeckoMarkupRect,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  RolodexEntryJson,
  RolodexEntryType,
} from 'types'

export type ZoneBounds = {
  x_start: number
  y_start: number
  x_end: number
  y_end: number
  unit: string
}

const ZONE_DEFAULT_COLOR = '#3B82F6'
const ZONE_FILL_OPACITY = 0.2
const ZONE_STROKE_WIDTH = 2

export type ZoneCanvasMark = Pick<ZoneParams, 'title' | 'color'> & {
  drawing_bounds: ZoneRectangle
}

export function zoneBoxesToCanvasMarks(
  zones: ReadonlyArray<Pick<ZoneParams, 'title' | 'color' | 'drawing_bounds'>>
): ZoneCanvasMark[] {
  return zones.flatMap((zone) =>
    (zone.drawing_bounds ?? []).map((drawing_bounds, index) => ({
      title: zoneBoxLabel(zone.title, index),
      color: zone.color,
      drawing_bounds,
    }))
  )
}

export const ZONE_COLORS = [
  ZONE_DEFAULT_COLOR,
  '#EF4444',
  '#10B981',
  '#F59E0B',
  '#8B5CF6',
]

function hexToRgba(hex: string, alpha: number): string {
  const cleaned = hex.replace('#', '')
  const r = parseInt(cleaned.substring(0, 2), 16)
  const g = parseInt(cleaned.substring(2, 4), 16)
  const b = parseInt(cleaned.substring(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * Draws zone rectangles and labels onto a canvas context.
 * Uses the same visual style as the SVG zone overlays in SmartImageViewer.
 */
export function drawZonesOnCanvas(
  ctx: CanvasRenderingContext2D,
  imageHeight: number,
  zones: ReadonlyArray<ZoneCanvasMark>
): void {
  for (const zone of zones) {
    const { x_start, y_start, x_end, y_end } = zone.drawing_bounds
    const color = zone.color || ZONE_DEFAULT_COLOR
    const w = x_end - x_start
    const h = y_end - y_start

    ctx.fillStyle = hexToRgba(color, ZONE_FILL_OPACITY)
    ctx.fillRect(x_start, y_start, w, h)

    ctx.strokeStyle = color
    ctx.lineWidth = ZONE_STROKE_WIDTH
    ctx.strokeRect(x_start, y_start, w, h)

    const fontSize = Math.max(14, Math.round(imageHeight / 50))
    ctx.font = `600 ${fontSize}px sans-serif`
    ctx.fillStyle = '#ffffff'
    ctx.shadowColor = 'rgba(0,0,0,0.8)'
    ctx.shadowBlur = 3
    ctx.shadowOffsetX = 1
    ctx.shadowOffsetY = 1
    ctx.fillText(zone.title, x_start + 6, y_start + fontSize + 4)
    ctx.shadowColor = 'transparent'
    ctx.shadowBlur = 0
    ctx.shadowOffsetX = 0
    ctx.shadowOffsetY = 0
  }
}

export function zonesToMarkupLayers(
  zones: ReadonlyArray<ZoneParams>,
  componentId: string,
  orgId: string,
  siteId: string,
  unitId: string
): GeckoMarkupLayer[] {
  return zones
    .filter((zone) => zone.bounds && zone.componentId === componentId)
    .map((zone, index) => {
      const color = zone.color || ZONE_COLORS[index % ZONE_COLORS.length]

      const markupLayerJson: RolodexEntryJson = {
        id: `zone-${zone.id}`,
        type: RolodexEntryType.MARKUP_LAYER,
        data: {
          name: zone.title,
          color: color,
          category: 'user-added',
          geometry: {
            x_measurement_unit: zone.bounds!.unit,
            y_measurement_unit: zone.bounds!.unit,
          },
          primitives: {
            [zone.id]: {
              kind: 'rect',
              x_start: zone.bounds!.x_start,
              y_start: zone.bounds!.y_start,
              x_end: zone.bounds!.x_end,
              y_end: zone.bounds!.y_end,
            },
          },
          schema_version: 5,
        },
        tags: {
          component: componentId,
          organization: orgId,
          site: siteId,
          unit: unitId,
        },
        index: {},
        generation: 1,
        updated_at: new Date().toISOString(),
        access_restriction: null,
      }

      return GeckoMarkupLayer.CreateFromJSON(markupLayerJson)
    })
}

export function markupRectToZoneBounds(primitive: GeckoMarkupRect): ZoneBounds {
  return {
    x_start: primitive.rect.x.start.num,
    y_start: primitive.rect.y.start.num,
    x_end: primitive.rect.x.end.num,
    y_end: primitive.rect.y.end.num,
    unit: primitive.rect.x.unit.id,
  }
}

export function calculateZoneArea(rect: GeckoComponentRect): number {
  const width = Math.abs(rect.x.end.num - rect.x.start.num)
  const height = Math.abs(rect.y.end.num - rect.y.start.num)

  const widthMeasurement = GeckoMeasurement.CreateFromValue(width, rect.x.unit)
  const heightMeasurement = GeckoMeasurement.CreateFromValue(
    height,
    rect.y.unit
  )

  const widthMeters = widthMeasurement.toNumber(GeckoMeasurementUnit.Meters)
  const heightMeters = heightMeasurement.toNumber(GeckoMeasurementUnit.Meters)

  return widthMeters * heightMeters
}

export function calculateZoneLength(rect: GeckoComponentRect): number {
  const width = Math.abs(rect.x.end.num - rect.x.start.num)
  const widthMeasurement = GeckoMeasurement.CreateFromValue(width, rect.x.unit)
  return widthMeasurement.toNumber(GeckoMeasurementUnit.Millimeters)
}

export function calculateZoneHeight(rect: GeckoComponentRect): number {
  const height = Math.abs(rect.y.end.num - rect.y.start.num)
  const heightMeasurement = GeckoMeasurement.CreateFromValue(
    height,
    rect.y.unit
  )
  return heightMeasurement.toNumber(GeckoMeasurementUnit.Millimeters)
}

export function createDrawingLayer(
  componentId: string,
  orgId: string,
  siteId: string,
  unitId: string,
  unit: string
): GeckoMarkupLayer {
  const drawingLayerJson: RolodexEntryJson = {
    id: 'temp-drawing-layer',
    type: RolodexEntryType.MARKUP_LAYER,
    data: {
      name: 'Drawing Zone',
      color: ZONE_DEFAULT_COLOR,
      category: 'user-added',
      geometry: {
        x_measurement_unit: unit,
        y_measurement_unit: unit,
      },
      primitives: {},
      schema_version: 5,
    },
    tags: {
      component: componentId,
      organization: orgId,
      site: siteId,
      unit: unitId,
    },
    index: {},
    generation: 1,
    updated_at: new Date().toISOString(),
    access_restriction: null,
  }

  return GeckoMarkupLayer.CreateFromJSON(drawingLayerJson)
}

export function findMarkupLayerForZone(
  zoneId: string,
  markupLayers: ReadonlyArray<GeckoMarkupLayer>
): GeckoMarkupLayer | undefined {
  return markupLayers.find((layer) => layer.id === `zone-${zoneId}`)
}
